'use client';
import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { useT } from '@/lib/useT';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallButton({ variant = 'header' }: { variant?: 'header' | 'footer' }) {
  const t = useT();
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already running as standalone (installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
      return;
    }
    const handler = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => { setInstalled(true); setPrompt(null); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (installed || !prompt) return null;

  const handleInstall = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') { setInstalled(true); setPrompt(null); }
  };

  if (variant === 'footer') {
    return (
      <li>
        <button
          onClick={handleInstall}
          className="flex items-center gap-1.5 text-sm transition-colors hover:text-ink"
          style={{ color: 'var(--c-muted)' }}>
          <Download size={13} />
          {t('pwa_install')}
        </button>
      </li>
    );
  }

  // Header variant — compact pill button
  return (
    <button
      onClick={handleInstall}
      title={t('pwa_install')}
      className="hidden lg:flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all hover:scale-[1.03] shrink-0"
      style={{
        background: 'rgba(37, 99, 235,0.1)',
        color: '#2563eb',
        border: '1px solid rgba(37, 99, 235,0.22)',
      }}>
      <Download size={13} strokeWidth={2.2} />
      {t('pwa_install_short')}
    </button>
  );
}
